import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';

const USER_CONTEXT_STORAGE_KEY = 'plynote_user_context_v1';
const RECOVERY_SESSION_LOCK_KEY = 'plynote_recovery_session_lock';
const SESSION_PERSISTENCE_MS = 30 * 24 * 60 * 60 * 1000;

const UserStateContext = createContext(undefined);
const UserDispatchContext = createContext(undefined);

const createInitialUserState = () => ({
	user_id: null,
	name: '',
	first_name: '',
	last_name: '',
	username: '',
	email: '',
	phone_number: '',
	auth_provider: '',
	access_token: '',
	refresh_token: '',
	session_expires_at: null,
	is_authenticated: false,
	is_loading: true,
	connection_status: 'disconnected',
	connection_error: '',
	role: '',
	avatar_url: '',
	created_at: null,
	updated_at: null,
	persisted_until: null,
	is_recovery_session: false,
});

const createLoggedOutUserState = () => ({
	...createInitialUserState(),
	is_loading: false,
});

const parseExpiryMs = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const asNumber = Number(value);
	if (Number.isFinite(asNumber)) return asNumber;
	const parsedMs = Date.parse(String(value));
	return Number.isFinite(parsedMs) ? parsedMs : null;
};

const readPersistedUserState = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const raw = window.localStorage.getItem(USER_CONTEXT_STORAGE_KEY);
		if (!raw) return null;

		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;

		return {
			user_id: parsed.user_id ?? null,
			name: parsed.name ?? '',
			first_name: parsed.first_name ?? '',
			last_name: parsed.last_name ?? '',
			username: parsed.username ?? '',
			email: parsed.email ?? '',
			phone_number: parsed.phone_number ?? '',
			auth_provider: parsed.auth_provider ?? '',
			access_token: parsed.access_token ?? '',
			refresh_token: parsed.refresh_token ?? '',
			session_expires_at: parsed.session_expires_at ?? null,
			is_authenticated: Boolean(parsed.is_authenticated),
			role: parsed.role ?? '',
			avatar_url: parsed.avatar_url ?? '',
			created_at: parsed.created_at ?? null,
			updated_at: parsed.updated_at ?? null,
			persisted_until: parsed.persisted_until ?? null,
		};
	} catch {
		return null;
	}
};

const isRecoverySessionLocked = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	try {
		return window.sessionStorage.getItem(RECOVERY_SESSION_LOCK_KEY) === '1';
	} catch {
		return false;
	}
};

const setRecoverySessionLock = (locked) => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		if (locked) {
			window.sessionStorage.setItem(RECOVERY_SESSION_LOCK_KEY, '1');
			return;
		}
		window.sessionStorage.removeItem(RECOVERY_SESSION_LOCK_KEY);
	} catch {
		// Ignore storage errors.
	}
};

const persistUserState = (state) => {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		const hasAuth = Boolean(state?.is_authenticated) && Boolean(state?.access_token);
		if (!hasAuth) {
			window.localStorage.removeItem(USER_CONTEXT_STORAGE_KEY);
			return;
		}

		const serializableState = {
			user_id: state.user_id,
			name: state.name,
			first_name: state.first_name,
			last_name: state.last_name,
			username: state.username,
			email: state.email,
			phone_number: state.phone_number,
			auth_provider: state.auth_provider,
			access_token: state.access_token,
			refresh_token: state.refresh_token,
			session_expires_at: state.session_expires_at,
			is_authenticated: state.is_authenticated,
			role: state.role,
			avatar_url: state.avatar_url,
			created_at: state.created_at,
			updated_at: state.updated_at,
			persisted_until: state.persisted_until || new Date(Date.now() + SESSION_PERSISTENCE_MS).toISOString(),
		};

		window.localStorage.setItem(USER_CONTEXT_STORAGE_KEY, JSON.stringify(serializableState));
	} catch {
		// Ignore persistence errors to keep auth flow resilient.
	}
};

const createInitialStateWithPersistence = () => {
	const base = createInitialUserState();
	const recoveryLocked = isRecoverySessionLocked();
	if (recoveryLocked) {
		return {
			...base,
			is_recovery_session: true,
		};
	}

	const persisted = readPersistedUserState();
	const persistedUntilMs = parseExpiryMs(persisted?.persisted_until);

	if (persisted && persistedUntilMs && persistedUntilMs <= Date.now()) {
		if (typeof window !== 'undefined') {
			try {
				window.localStorage.removeItem(USER_CONTEXT_STORAGE_KEY);
			} catch {
				// Ignore storage errors on expired session cleanup.
			}
		}
		return base;
	}

	if (!persisted) {
		return base;
	}

	return {
		...base,
		...persisted,
		is_loading: true,
		is_recovery_session: false,
		connection_status: persisted.is_authenticated ? 'connected' : base.connection_status,
		persisted_until: persisted.persisted_until || new Date(Date.now() + SESSION_PERSISTENCE_MS).toISOString(),
	};
};

export const UserProvider = ({ children }) => {
	const [userState, setUserState] = useState(createInitialStateWithPersistence);

	const setUser_id = useCallback((nextUserId) => {
		setUserState((currentState) => ({
			...currentState,
			user_id: nextUserId === undefined ? null : nextUserId,
		}));
	}, []);

	const setUserField = useCallback((field, value) => {
		if (!field || typeof field !== 'string') return;

		setUserState((currentState) => ({
			...currentState,
			[field]: value,
		}));
	}, []);

	const setUserProfile = useCallback((profile = {}) => {
		setUserState((currentState) => ({
			...currentState,
			user_id: profile.user_id === undefined ? currentState.user_id : profile.user_id,
			name: profile.name === undefined ? currentState.name : profile.name,
			first_name: profile.first_name === undefined ? currentState.first_name : profile.first_name,
			last_name: profile.last_name === undefined ? currentState.last_name : profile.last_name,
			username: profile.username === undefined ? currentState.username : profile.username,
			email: profile.email === undefined ? currentState.email : profile.email,
			phone_number: profile.phone_number === undefined ? currentState.phone_number : profile.phone_number,
			role: profile.role === undefined ? currentState.role : profile.role,
			avatar_url: profile.avatar_url === undefined ? currentState.avatar_url : profile.avatar_url,
			created_at: profile.created_at === undefined ? currentState.created_at : profile.created_at,
			updated_at: profile.updated_at === undefined ? currentState.updated_at : profile.updated_at,
		}));
	}, []);

	const setConnectionState = useCallback((connection = {}) => {
		const recoveryLocked = isRecoverySessionLocked();
		setUserState((currentState) => ({
			...currentState,
			is_authenticated: recoveryLocked
				? false
				: (connection.is_authenticated === undefined ? currentState.is_authenticated : Boolean(connection.is_authenticated)),
			is_loading: connection.is_loading === undefined ? currentState.is_loading : Boolean(connection.is_loading),
			connection_status: connection.connection_status === undefined ? currentState.connection_status : String(connection.connection_status || '').trim() || currentState.connection_status,
			connection_error: connection.connection_error === undefined ? currentState.connection_error : String(connection.connection_error || ''),
			auth_provider: connection.auth_provider === undefined ? currentState.auth_provider : String(connection.auth_provider || ''),
			session_expires_at: connection.session_expires_at === undefined ? currentState.session_expires_at : connection.session_expires_at,
			access_token: recoveryLocked
				? ''
				: (connection.access_token === undefined ? currentState.access_token : String(connection.access_token || '')),
			refresh_token: recoveryLocked
				? ''
				: (connection.refresh_token === undefined ? currentState.refresh_token : String(connection.refresh_token || '')),
			is_recovery_session: recoveryLocked,
			persisted_until: Boolean(connection.is_authenticated ?? currentState.is_authenticated)
				? (connection.persisted_until === undefined ? (currentState.persisted_until || new Date(Date.now() + SESSION_PERSISTENCE_MS).toISOString()) : connection.persisted_until)
				: null,
		}));
	}, []);

	const clearSensitiveAuthFields = useCallback(() => {
		setUserState((currentState) => ({
			...currentState,
			access_token: '',
			refresh_token: '',
		}));
	}, []);

	const resetUserState = useCallback(() => {
		setUserState(createLoggedOutUserState());
		setRecoverySessionLock(false);
		if (typeof window !== 'undefined') {
			try {
				window.localStorage.removeItem(USER_CONTEXT_STORAGE_KEY);
			} catch {
				// Ignore storage errors on reset.
			}
		}
	}, []);

	const logout = useCallback(async () => {
		resetUserState();
		if (supabase && supabase.auth) {
			try {
				await supabase.auth.signOut();
			} catch {
				// Ignore sign-out errors
			}
		}
	}, [resetUserState]);

	useEffect(() => {
		persistUserState(userState);
	}, [userState]);

	useEffect(() => {
		if (!supabase) {
			return undefined;
		}

		let isMounted = true;
		let initialSessionResolved = false;
		let bootstrapFinalized = false;

		const applyRecoveryLockState = () => {
			setRecoverySessionLock(true);
			setUserState((currentState) => ({
				...currentState,
				user_id: null,
				name: '',
				first_name: '',
				last_name: '',
				username: '',
				email: '',
				phone_number: '',
				auth_provider: '',
				role: '',
				avatar_url: '',
				created_at: null,
				updated_at: null,
				is_authenticated: false,
				is_loading: false,
				connection_status: 'disconnected',
				connection_error: '',
				access_token: '',
				refresh_token: '',
				session_expires_at: null,
				persisted_until: null,
				is_recovery_session: true,
			}));
		};

		const applySession = (session, source = 'listener') => {
			if (!isMounted) {
				return;
			}

			if (!session?.access_token && source === 'listener' && !initialSessionResolved) {
				return;
			}

			if (!session?.access_token) {
				setRecoverySessionLock(false);
				setUserState((currentState) => ({
					...currentState,
					is_authenticated: false,
					is_loading: false,
					connection_status: 'disconnected',
					connection_error: '',
					access_token: '',
					refresh_token: '',
					session_expires_at: null,
					persisted_until: null,
					is_recovery_session: false,
				}));
				return;
			}

			if (isRecoverySessionLocked()) {
				applyRecoveryLockState();
				return;
			}

			setUserState((currentState) => ({
				...currentState,
				user_id: session?.user?.id || currentState.user_id,
				is_authenticated: true,
				is_loading: false,
				connection_status: 'connected',
				connection_error: '',
				access_token: session.access_token,
				refresh_token: session.refresh_token || '',
				session_expires_at: session.expires_at || null,
				persisted_until: new Date(Date.now() + SESSION_PERSISTENCE_MS).toISOString(),
				is_recovery_session: false,
			}));
		};

		const isPasswordRecoveryUrl = () =>
			typeof window !== 'undefined'
			&& (() => {
				const hash = String(window.location?.hash || '');
				const searchParams = new URLSearchParams(window.location?.search || '');
				return searchParams.get('mode') === 'reset'
					|| searchParams.get('type') === 'recovery'
					|| searchParams.has('token_hash')
					|| hash.includes('type=recovery');
			})();

		const finalizeBootstrapAsDisconnected = async () => {
			if (bootstrapFinalized || !isMounted) {
				return;
			}

			// Give Supabase a brief window to finish hydrating session state.
			await new Promise((resolve) => setTimeout(resolve, 120));
			if (bootstrapFinalized || !isMounted) {
				return;
			}

			// One last read prevents transient races where getSession() returns null
			// just before the client finishes restoring a valid session.
			try {
				const { data: retryData } = await supabase.auth.getSession();
				if (retryData?.session?.access_token) {
					initialSessionResolved = true;
					bootstrapFinalized = true;
					applySession(retryData.session, 'bootstrap-retry');
					return;
				}
			} catch {
				// Fall through to disconnected state.
			}

			initialSessionResolved = true;
			bootstrapFinalized = true;
			applySession(null, 'bootstrap');
		};

		supabase.auth.getSession()
			.then(async ({ data }) => {
				if (data?.session?.access_token) {
					initialSessionResolved = true;
					bootstrapFinalized = true;
					if (isPasswordRecoveryUrl()) {
						applyRecoveryLockState();
						return;
					}
					applySession(data.session, 'bootstrap');
					return;
				}

				// No active Supabase session — try to restore from persisted refresh token.
				// This happens when tokens were issued by the backend (email/password login)
				// and were never registered with the browser Supabase client.
				const persisted = readPersistedUserState();
				if (persisted?.refresh_token && persisted?.is_authenticated) {
					try {
						const { data: restored, error } = await supabase.auth.setSession({
							access_token: persisted.access_token || '',
							refresh_token: persisted.refresh_token,
						});
						if (!error && restored?.session?.access_token) {
							initialSessionResolved = true;
							bootstrapFinalized = true;
							applySession(restored.session, 'bootstrap');
							return;
						}
					} catch {
						// Refresh token is expired or invalid — fall through to disconnect.
					}
				}

				await finalizeBootstrapAsDisconnected();
			})
			.catch(async () => {
				await finalizeBootstrapAsDisconnected();
			});

		const { data: authSubscription } = supabase.auth.onAuthStateChange((event, session) => {
			if (event === 'PASSWORD_RECOVERY' || (isPasswordRecoveryUrl() && event === 'SIGNED_IN')) {
				applyRecoveryLockState();
				return;
			}
			applySession(session || null, 'listener');
		});

		return () => {
			isMounted = false;
			authSubscription?.subscription?.unsubscribe?.();
		};
	}, []);

	const stateValue = useMemo(() => ({
		...userState,
	}), [userState]);

	const dispatchValue = useMemo(() => ({
		setUser_id,
		setUserField,
		setUserProfile,
		setConnectionState,
		clearSensitiveAuthFields,
		resetUserState,
		logout,
	}), [
		clearSensitiveAuthFields,
		logout,
		resetUserState,
		setConnectionState,
		setUserField,
		setUserProfile,
		setUser_id,
	]);

	return (
		<UserStateContext.Provider value={stateValue}>
			<UserDispatchContext.Provider value={dispatchValue}>{children}</UserDispatchContext.Provider>
		</UserStateContext.Provider>
	);
};

export const useUserState = () => {
	const context = useContext(UserStateContext);

	if (!context) {
		throw new Error('useUserState must be used within a UserProvider.');
	}

	return context;
};

export const useUserDispatch = () => {
	const context = useContext(UserDispatchContext);

	if (!context) {
		throw new Error('useUserDispatch must be used within a UserProvider.');
	}

	return context;
};

export const useUserContext = () => {
	const state = useUserState();
	const dispatch = useUserDispatch();

	return useMemo(() => ({ ...state, ...dispatch }), [state, dispatch]);
};

export default UserStateContext;
